import type {
    ApiErrorResponse,
    ApiSuccessResponse,
} from '@calcom/atoms/dist/packages/platform/types/api';
import useEditor from 'client/widget-components/customHooks/useEditor';
import { useIsHydrated } from 'client/widget-components/customHooks/useIsHydrated';
import { buyBooking } from 'client/widget-components/ecom/services/snipcartService';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useBookingContext } from '../../../Context';
import useCalEventData from '../../../hooks/useCalEventData';
import { BookerStateContext } from '../../../hooks/useRuntimeCalBookingState';
import { Booker, LazyBookerWrapper } from '../../../LazyBooker';
import {
    createBookerResponseLoggingContext,
    logBookingError,
    logBookingInfo,
} from '../../../loggingUtils';
import {
    BookerStoreValues,
    Booking,
    CalBookerStep,
    CreateBookingInput,
} from '../../../types';
import {
    bookingObjToEcomConverter,
    createDryRunBookingPayload,
    getBookerClassNames,
    getBookerMetadata,
    shouldUsePaidBooking,
    validateBookerReceivedParams,
} from '../../../utils';
import BookerBackButton from './BookerBackButton';
import BookerSkeleton from './BookerSkeleton';
import BookingMarkup from './BookingMarkup';
import BookingPricingMetaSection from './BookingPricingMetaSection';
import { BookingCancellation } from './ConfirmationModal/BookingCancellation';
import BookingConfirmContainer from './ConfirmationModal/BookingConfirmContainer';
import RescheduleModal from './ConfirmationModal/RescheduleModal';

const BookerStep = () => {
    const hydrated = useIsHydrated();
    const bookerContext = useBookingContext();
    const editor = useEditor();
    const [bookingState, bookingActions] = useContext(BookerStateContext);

    const [calBookerStep, setCalBookerStep] = useState<CalBookerStep | null>(
        null
    );

    const { organizationId, teamId, designStyle, isBookingDryRun, clientId } =
        bookerContext;

    const {
        isLoading,
        confirmationData,
        rescheduleUid,
        cancelBookingId,
        calEventData,
        confirmButtonDisabled,
        language,
        apiUrl,
        bookerKey,
        eventSlug,
    } = bookingState;

    const {
        setCalEventData,
        setConfirmButtonDisabled,
        resetBookerState,
        clearConfirmationData,
        onBookingSuccess,
        setRescheduleUid,
        setIsLoading,
        setCancelBookingId,
    } = bookingActions;

    useCalEventData(
        {
            teamId: teamId || undefined,
            eventSlug: eventSlug || '',
            organizationId,
        },
        {
            onSuccess: setCalEventData,
        }
    );

    const isBookingActive = !!eventSlug;

    // validate all required props are present for booker atom when booking is active
    validateBookerReceivedParams(bookerContext, isBookingActive);

    const bookerClassNames = useMemo(
        () => getBookerClassNames(designStyle),
        [designStyle]
    );

    const rescheduleBooking = () => {
        if (!confirmationData?.uid) {
            return;
        }
        setRescheduleUid(confirmationData.uid);
        clearConfirmationData();
    };

    const handleBookingSuccess = (res: ApiSuccessResponse<any>) => {
        // Extract booking data from the API response structure
        const bookingData = res.data as Booking;
        onBookingSuccess(bookingData);
    };

    const handleCreatePaidBooking = async (
        createBookingInput: CreateBookingInput
    ) => {
        try {
            setConfirmButtonDisabled(true);

            // We should allow to proceed to checkout for disabled Paid feature (mocked booking feature)
            if (!editor.insideEditor() && calEventData) {
                await buyBooking(
                    bookingObjToEcomConverter(createBookingInput, calEventData)
                );
                resetBookerState();
            } else {
                onBookingSuccess(createDryRunBookingPayload());
                logBookingInfo(
                    'Tried to create paid booking without calEventData',
                    {
                        additionalInfoToLog: bookerContext,
                    }
                );
            }
        } catch (error) {
            logBookingError('Failed to create paid booking', {
                errorMessage: (error as Error).message,
                additionalInfoToLog: bookerContext,
            });
        } finally {
            setConfirmButtonDisabled(false);
        }
    };

    const onBookerStateChange = useCallback(
        (bookerState: BookerStoreValues) => {
            setCalBookerStep(bookerState.state as CalBookerStep);
            if (
                bookerState.state === CalBookerStep.SELECTING_TIME &&
                isLoading
            ) {
                setIsLoading(false);
            }
        },
        [isLoading, setIsLoading]
    );

    // We want to render the booker only after the component is hydrated on
    // client side since it's a lazy loaded component and we want to keep the
    // server and client rendering consistent.
    const shouldRenderBooker = hydrated && isBookingActive;
    const shouldRenderLoadingSkeleton = isBookingActive && isLoading;
    const shouldRenderBookingMarkup = !isBookingActive;
    const showRescheduleModal = !isBookingDryRun && rescheduleUid;

    const calBookerWrapperClassNames: string[] = [designStyle];
    if (isBookingActive) {
        calBookerWrapperClassNames.push(
            isLoading ? 'booker-loading' : 'booker-ready'
        );
    }

    const commonBookerProps = {
        eventSlug: eventSlug || '',
        customClassNames: bookerClassNames,
        // If the booking is a paid type and the widget is not in reschedule mode, we want to use the paid booking flow
        // For reschedule mode - use the free booking flow since the booking is already paid
        ...(shouldUsePaidBooking(bookerContext) && !rescheduleUid
            ? {
                  handleCreateBooking: handleCreatePaidBooking,
                  handleSlotReservation: async () => {
                      // no-op
                      // need to be passed to the booker atom to avoid internal reservation operation
                  },
              }
            : { onCreateBookingSuccess: handleBookingSuccess }),
        onCreateBookingError: (resOrError: ApiErrorResponse | Error) => {
            logBookingError('Failed to create booking inside Booker atom', {
                ...(resOrError instanceof Error
                    ? {
                          errorMessage: resOrError.message,
                          additionalInfoToLog: bookerContext,
                      }
                    : createBookerResponseLoggingContext(
                          bookerContext,
                          resOrError
                      )),
            });
        },
        onReserveSlotError: (res: ApiErrorResponse) => {
            logBookingError(
                'Failed to reserve slot inside Booker atom',
                createBookerResponseLoggingContext(bookerContext, res)
            );
        },
        onDeleteSlotError: (res: ApiErrorResponse) => {
            logBookingError(
                'Failed to delete slot inside Booker atom',
                createBookerResponseLoggingContext(bookerContext, res)
            );
        },
        showNoAvailabilityDialog: false,
        roundRobinHideOrgAndTeam: true,
        /**
         * When true the booker still displays slots
         * when the third party calendars credentials are invalid or expired,
         * Booker may show stale availability when enabled
         * */
        silentlyHandleCalendarFailures: true,
        eventMetaChildren: (
            <>
                <BookingPricingMetaSection />
                {!isLoading && (
                    <BookerBackButton
                        key={calBookerStep}
                        calBookerStep={calBookerStep}
                    />
                )}
            </>
        ),
        metadata: getBookerMetadata(bookerContext, calEventData),
        confirmButtonDisabled,
    };

    return (
        <div
            className={`booker-wrapper ${calBookerWrapperClassNames.join(' ')}`}
        >
            {shouldRenderBooker && (
                <LazyBookerWrapper
                    clientId={clientId}
                    apiUrl={apiUrl}
                    language={language}
                >
                    <Booker
                        key={bookerKey}
                        isTeamEvent
                        teamId={teamId!}
                        isBookingDryRun={isBookingDryRun}
                        onBookerStateChange={onBookerStateChange}
                        {...commonBookerProps}
                    />
                    {showRescheduleModal && (
                        <RescheduleModal
                            bookerProps={{
                                isTeamEvent: true,
                                teamId: teamId!,
                                bookingUid: rescheduleUid,
                                ...commonBookerProps,
                            }}
                            onClose={() => setRescheduleUid(null)}
                        />
                    )}
                </LazyBookerWrapper>
            )}
            {confirmationData && (
                <BookingConfirmContainer
                    confirmationData={confirmationData}
                    closeConfirmation={clearConfirmationData}
                    rescheduleBooking={rescheduleBooking}
                    setCancelBookingId={setCancelBookingId}
                />
            )}
            {cancelBookingId && (
                <BookingCancellation
                    bookingUid={cancelBookingId}
                    closeCancellation={() => setCancelBookingId(null)}
                />
            )}
            {shouldRenderLoadingSkeleton && <BookerSkeleton />}
            {shouldRenderBookingMarkup && <BookingMarkup />}
        </div>
    );
};

export default BookerStep;
